import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { z } from "zod";
import {
  CloseButton,
  PrimaryButtonFitContainer,
  TertiaryButtonFitContainer,
} from "../../../components/Buttons/Buttons";
import { CheckBoxNoLabel } from "../../../components/CheckBoxes/CheckBoxes";
import { GenericDialogBody } from "../../../components/ConfirmDialog/ConfirmDialog";
import { CheckBoxContainer } from "../../../components/Form/Form";
import { TextArea } from "../../../components/TextArea/TextArea";
import { H3, RegularText } from "../../../components/Typography/Typography";
import { useFormWrapper } from "../../../util/util";
import {
  CheckboxContainerWrapper,
  CheckBoxPrintLabel,
  ErrorMessage,
} from "./LeadToBlockModal";
import type { DeepMap, FieldError, FieldValues } from "react-hook-form";
import { useMemo, useState } from "react";
import { XIcon } from "../../../components/Icons/Icons";
import { spacingTokens } from "../../../designTokens";

export const DialogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const HeaderContainer = styled.div<{ showAlignCloseButton?: boolean }>`
  display: flex;
  justify-content: "space-between";
`;

export interface FollowedExternallyFormValues extends FieldValues {
  email: boolean;
  phone: boolean;
  fax: boolean;
  text: boolean;
  reasonForFollowedExternally: string;
}

interface FollowedExternallyFormErrors
  extends DeepMap<FollowedExternallyFormValues, FieldError> {
  at_least_one_checked?: FieldError;
}

export const FollowedExternallyModal = ({
  showModal,
  closeModal,
  handleFollowedExternally,
}: {
  showModal: boolean;
  closeModal: () => void;
  handleFollowedExternally: (data: FollowedExternallyFormValues) => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [activeCheckbox, setActiveCheckbox] = useState<string | null>(null);

  const followedExternallySchema = z
    .object({
      email: z.boolean(),
      phone: z.boolean(),
      fax: z.boolean(),
      text: z.boolean(),
      reasonForFollowedExternally: z
        .string()
        .min(1, t("This is a required field")),
    })
    .superRefine((data, ctx) => {
      const { email, phone, fax, text } = data;

      if (![email, phone, fax, text].some(Boolean)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("At least one checkbox must be selected"),
          path: ["at_least_one_checked"],
        });
      }
    });

  const followedExternallyDefaultValue = {
    email: false,
    phone: false,
    fax: false,
    text: false,
  };

  const followedExternallyCheckBox = [
    {
      name: "email",
      label: t("Email"),
      required: true,
    },
    {
      name: "phone",
      label: t("Phone"),
      required: true,
    },
    {
      name: "fax",
      label: t("Fax"),
      required: true,
    },
    {
      name: "text",
      label: t("Text"),
      required: true,
    },
  ];

  const { handleSubmit, register, watch, formState, errors } = useFormWrapper<
    FollowedExternallyFormValues,
    FollowedExternallyFormErrors
  >({
    resolver: zodResolver(followedExternallySchema),
    defaultValues: followedExternallyDefaultValue || {},
  });

  const checkboxValues = watch(["email", "phone", "fax", "text"]);
  const isAnyChecked = useMemo(
    () => Object.values(checkboxValues).some((value) => value === true),
    [checkboxValues]
  );

  const validationError: boolean = errors.at_least_one_checked && !isAnyChecked;
  const validationMessage = errors?.at_least_one_checked;

  const isDisableButton: boolean = validationMessage && !formState.isValid;

  const handleCheckboxChange = (name: string, isChecked: boolean) => {
    if (isChecked) {
      setActiveCheckbox(name);
    } else if (activeCheckbox === name) {
      setActiveCheckbox(null);
    }
  };

  return (
    <div>
      <GenericDialogBody
        show={showModal}
        closeDialog={closeModal}
        showCloseButton={false}
      >
        <DialogWrapper>
          <HeaderContainer>
            <H3 style={{ margin: `0 0 ${spacingTokens.spacing_md} 0` }}>
              {t("Are you sure you followed up on this lead externally?")}
            </H3>
            <CloseButton onClick={closeModal}>
              <XIcon />
            </CloseButton>
          </HeaderContainer>

          <CheckboxContainerWrapper>
            <RegularText style={{ color: theme.primaryTextColor }}>
              {t("Please select the method of following up externally")}
            </RegularText>
            {followedExternallyCheckBox.map((checkbox, index) => {
              const isDisabled =
                activeCheckbox !== null && activeCheckbox !== checkbox.name;
              return (
                <CheckBoxContainer key={index}>
                  <div style={{ minWidth: "12px" }}>
                    <CheckBoxNoLabel
                      name={checkbox.name}
                      id={checkbox.name}
                      ref={register({ required: checkbox.required })}
                      style={{ marginRight: "10px" }}
                      error={checkbox.required && validationError}
                      disabled={isDisabled}
                      onChange={(e) =>
                        handleCheckboxChange(checkbox.name, e.target.checked)
                      }
                    />
                  </div>
                  <CheckBoxPrintLabel
                    htmlFor={checkbox.name}
                    hasError={checkbox.required && validationError}
                  >
                    {checkbox.label}
                  </CheckBoxPrintLabel>
                </CheckBoxContainer>
              );
            })}
            {validationError && (
              <ErrorMessage>{t(validationMessage?.message)}</ErrorMessage>
            )}
            <TextArea
              name={"reasonForFollowedExternally"}
              label={t("Note")}
              theref={register({ required: true })}
              formState={formState}
              required={false}
              errors={errors}
            />
          </CheckboxContainerWrapper>

          <div style={{ display: "flex", gap: spacingTokens.spacing_xl }}>
            <TertiaryButtonFitContainer onClick={closeModal} type="button">
              {t("Cancel")}
            </TertiaryButtonFitContainer>
            <PrimaryButtonFitContainer
              onClick={handleSubmit((data) => handleFollowedExternally(data))}
              disabled={isDisableButton}
            >
              {t("Confirm")}
            </PrimaryButtonFitContainer>
          </div>
        </DialogWrapper>
      </GenericDialogBody>
    </div>
  );
};
